import * as React from "react";
import { useConversationRecording } from "../hooks/useConversationRecording";
import { useCompleteConversationLog } from "../api/mutations/useConversationLogMutations";
import { AllChartsData } from "../hooks/useAllChartsData";
import { useAllChartsDataContext } from "./AllChartsDataContext";

interface HHERAllyContextValue {
  isPanelOpen: boolean;
  isRecording: boolean;
  isUploading: boolean;
  activeConversationLogId: string | null;
  transcript: string;
  focusedSectionCode: string | undefined;
  openPanel: (sectionCode?: string) => void;
  closePanel: () => void;
  startListening: () => Promise<void>;
  stopListening: () => Promise<void>;
  setTranscript: (text: string) => void;
}

interface HHERAllyProviderProps {
  encounterId: string;
  chartCode: string;
  children: React.ReactNode;
}

const HHERAllyContext = React.createContext<HHERAllyContextValue | undefined>(
  undefined
);

function useHHERAllyContext(): HHERAllyContextValue {
  const context = React.useContext(HHERAllyContext);

  if (context === undefined) {
    throw new Error(
      "useHHERAllyContext must be used within an HHERAllyProvider"
    );
  }

  return context;
}

const MAX_CHART_STRUCTURE_SECTIONS = 50;
const MAX_FIELDS_PER_SECTION = 200;
const MAX_AVAILABLE_ORDERS = 500;

function buildChartStructure(
  allChartsData?: AllChartsData
): Record<string, string[]> | null {
  if (!allChartsData?.charts?.length) {
    return null;
  }

  const structure: Record<string, string[]> = {};
  for (const chart of allChartsData.charts) {
    for (const section of chart.sections ?? []) {
      const fields = (section.subsections ?? []).flatMap((subsection) =>
        (subsection.fields ?? []).map((field) => field.label)
      );
      structure[section.sectionName] = [
        ...(structure[section.sectionName] ?? []),
        ...fields,
      ];
    }
  }

  const trimmed: Record<string, string[]> = {};
  Object.entries(structure)
    .slice(0, MAX_CHART_STRUCTURE_SECTIONS)
    .forEach(([sectionName, fields]) => {
      trimmed[sectionName] = fields.slice(0, MAX_FIELDS_PER_SECTION);
    });
  return trimmed;
}

const HHERAllyProvider: React.FC<HHERAllyProviderProps> = ({
  encounterId,
  chartCode,
  children,
}) => {
  const [isPanelOpen, setIsPanelOpen] = React.useState<boolean>(false);
  const [transcript, setTranscript] = React.useState<string>("");
  const [focusedSectionCode, setFocusedSectionCode] = React.useState<
    string | undefined
  >(undefined);

  const {
    isRecording,
    isUploading,
    startListening: startRecording,
    stopListening: stopRecording,
    conversationLogId,
  } = useConversationRecording({ encounterId, chartCode });
  const completeMutation = useCompleteConversationLog();
  const { allChartsData, allOrderNames } = useAllChartsDataContext();

  const openPanel = React.useCallback((sectionCode?: string) => {
    setFocusedSectionCode(sectionCode);
    setIsPanelOpen(true);
  }, []);

  const closePanel = React.useCallback(() => {
    setFocusedSectionCode(undefined);
    setIsPanelOpen(false);
  }, []);

  const handleSetTranscript = React.useCallback((text: string) => {
    setTranscript(text);
  }, []);

  const startListening = React.useCallback(async () => {
    await startRecording();
  }, [startRecording]);

  const stopListening = React.useCallback(async () => {
    const { audioDuration } = await stopRecording();

    if (!conversationLogId) {
      return;
    }

    await completeMutation.mutateAsync({
      conversationLogId,
      encounterId,
      request: {
        transcriptText: "",
        audioDurationSeconds: Math.max(1, audioDuration),
        chartStructure: buildChartStructure(allChartsData),
        availableOrders: (allOrderNames ?? []).slice(0, MAX_AVAILABLE_ORDERS),
      },
    });
  }, [
    stopRecording,
    completeMutation,
    conversationLogId,
    encounterId,
    allChartsData,
    allOrderNames,
  ]);

  const value: HHERAllyContextValue = {
    isPanelOpen,
    isRecording,
    isUploading,
    activeConversationLogId: conversationLogId,
    transcript,
    focusedSectionCode,
    openPanel,
    closePanel,
    startListening,
    stopListening,
    setTranscript: handleSetTranscript,
  };

  return (
    <HHERAllyContext.Provider value={value}>
      {children}
    </HHERAllyContext.Provider>
  );
};

const HHERAllyContextProvider = HHERAllyProvider;

export { HHERAllyProvider, HHERAllyContextProvider, useHHERAllyContext };
export type { HHERAllyContextValue, HHERAllyProviderProps };
